import React, { useMemo } from 'react';
import { CategoryPill, Card, SectionHeader } from '../../../components/ui';
import { useIssuesForSubject } from '../../issues/useIssues';
import type { SubjectType } from '../../../types/issues';

export interface InfluenceArea {
  label: string;
  color?: string | null;
  /** 0–100 */
  score: number;
}

export interface Area {
  key: string;
  label: string;
  color?: string | null;
  /** What this is read from: where they carry weight, or what they engage with */
  from: 'influence' | 'issues';
  detail: string;
  sort: number;
}

/**
 * The subjects an entity is known for.
 *
 * Read from two places the directory already holds: the areas where their
 * influence has been assessed, and the issues they have taken a position on.
 * Nothing is shown when neither has anything to say — an empty heading tells
 * a reader less than no heading at all.
 */
export function areasOf(
  influence: InfluenceArea[],
  issues: Array<{ categoryLabel?: string | null; category?: string | null; tags?: string[] }>,
): Area[] {
  const areas: Area[] = [];
  for (const area of influence) {
    if (!area.label || area.score <= 0) continue;
    areas.push({
      key: `influence:${area.label.toLowerCase()}`,
      label: area.label,
      color: area.color,
      from: 'influence',
      detail: `Influence ${Math.round(area.score)}/100`,
      sort: 1000 + Math.round(area.score),
    });
  }
  const counted = new Map<string, { label: string; count: number }>();
  for (const issue of issues) {
    const label = issue.categoryLabel || issue.category;
    if (!label) continue;
    const key = label.toLowerCase();
    const held = counted.get(key) ?? { label, count: 0 };
    held.count += 1;
    counted.set(key, held);
  }
  counted.forEach((held, key) => {
    // An area already named by an influence score is not repeated
    if (areas.some((area) => area.label.toLowerCase() === key)) {
      return;
    }
    areas.push({
      key: `issues:${key}`,
      label: held.label,
      from: 'issues',
      detail: `${held.count} issue${held.count === 1 ? '' : 's'}`,
      sort: held.count,
    });
  });
  return areas.sort((a, b) => b.sort - a.sort || a.label.localeCompare(b.label));
}

export const AreasOfInterest: React.FC<{
  subjectType: SubjectType;
  subjectId: string | undefined;
  influence?: InfluenceArea[];
  className?: string;
}> = ({ subjectType, subjectId, influence = [], className = '' }) => {
  const { rows, loading, failed } = useIssuesForSubject(subjectType, subjectId);
  const areas = useMemo(
    () => areasOf(influence, failed ? [] : rows.map((row) => row.issue)),
    [influence, rows, failed],
  );

  if (loading && !influence.length) return null;
  if (!areas.length) return null;

  return (
    <section className={className}>
      <SectionHeader title="Areas of interest" />
      <Card padded>
        <ul className="flex flex-wrap gap-1.5">
          {areas.map((area) => (
            <li key={area.key}>
              <CategoryPill label={area.label} color={area.color} title={area.detail} />
            </li>
          ))}
        </ul>
        <p className="mt-2.5 text-[11.5px] leading-snug text-neutral-400">
          Where influence has been assessed, and the issues they have taken a position on.
        </p>
      </Card>
    </section>
  );
};
